import React, { useState } from 'react';

interface APIKey {
  id: string;
  name: string;
  key: string;
  maskedKey: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  status: 'active' | 'revoked' | 'expired';
  permissions: string[];
  requestCount: number;
  rateLimit: number;
}

export const APIKeysPage: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState<string[]>(['read']);
  const [generatedKey, setGeneratedKey] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Mock data
  const apiKeys: APIKey[] = [
    {
      id: '1',
      name: 'Production API Key',
      key: 'gp_live_xk3j8h2k9d8s7a6f5g4h3j2k1l0m9n8',
      maskedKey: 'gp_live_****************************n8',
      createdAt: '2026-01-15',
      lastUsedAt: '2026-01-27 10:30 AM',
      expiresAt: null,
      status: 'active',
      permissions: ['read', 'write', 'campaigns', 'analytics'],
      requestCount: 45230,
      rateLimit: 10000,
    },
    {
      id: '2',
      name: 'Development Key',
      key: 'gp_test_a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6',
      maskedKey: 'gp_test_****************************p6',
      createdAt: '2026-01-10',
      lastUsedAt: '2026-01-26 03:45 PM',
      expiresAt: '2026-07-10',
      status: 'active',
      permissions: ['read', 'campaigns'],
      requestCount: 12450,
      rateLimit: 1000,
    },
    {
      id: '3',
      name: 'Analytics Dashboard',
      key: 'gp_live_9z8y7x6w5v4u3t2s1r0q9p8o7n6m5l4',
      maskedKey: 'gp_live_****************************l4',
      createdAt: '2026-01-05',
      lastUsedAt: null,
      expiresAt: null,
      status: 'revoked',
      permissions: ['read', 'analytics'],
      requestCount: 3210,
      rateLimit: 5000,
    },
  ];

  const availablePermissions = [
    { id: 'read', label: 'Read', description: 'View campaigns and data' },
    { id: 'write', label: 'Write', description: 'Create and update campaigns' },
    { id: 'delete', label: 'Delete', description: 'Delete campaigns and data' },
    { id: 'campaigns', label: 'Campaigns', description: 'Full campaign management' },
    { id: 'analytics', label: 'Analytics', description: 'Access analytics data' },
    { id: 'webhooks', label: 'Webhooks', description: 'Manage webhooks' },
  ];

  const handleCreateKey = () => {
    // TODO: Call API to create key
    const newKey = `gp_live_${Math.random().toString(36).substring(2, 34)}`;
    setGeneratedKey(newKey);
    setShowCreateModal(false);
    setShowKeyModal(true);
    setNewKeyName('');
    setNewKeyPermissions(['read']);
  };

  const handleCopyKey = (key: string, id: string) => {
    navigator.clipboard.writeText(key);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleRevokeKey = (id: string) => {
    if (confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      console.log('Revoking key:', id);
      // TODO: Call API
    }
  };

  const handleTogglePermission = (permission: string) => {
    if (newKeyPermissions.includes(permission)) {
      setNewKeyPermissions(newKeyPermissions.filter(p => p !== permission));
    } else {
      setNewKeyPermissions([...newKeyPermissions, permission]);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'revoked': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'expired': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">API Keys</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage API keys for programmatic access to GP Ads platform
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-gp-sungold text-white rounded-lg hover:bg-gp-sungold-600"
        >
          + Create API Key
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Keys</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {apiKeys.length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {apiKeys.filter(k => k.status === 'active').length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Requests</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {(apiKeys.reduce((sum, k) => sum + k.requestCount, 0) / 1000).toFixed(1)}k
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Rate Limit</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {(apiKeys[0]?.rateLimit || 0).toLocaleString()}/day
          </p>
        </div>
      </div>

      {/* API Keys List */}
      <div className="space-y-4">
        {apiKeys.map((apiKey) => (
          <div
            key={apiKey.id}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {apiKey.name}
                  </h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(apiKey.status)}`}>
                    {apiKey.status}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <code className="px-3 py-2 bg-gray-100 dark:bg-gray-900 rounded font-mono text-sm text-gray-900 dark:text-white flex-1">
                      {apiKey.maskedKey}
                    </code>
                    <button
                      onClick={() => handleCopyKey(apiKey.key, apiKey.id)}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      {copiedId === apiKey.id ? (
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">Created</p>
                    <p className="font-medium text-gray-900 dark:text-white">{apiKey.createdAt}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">Last Used</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {apiKey.lastUsedAt || 'Never'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">Requests</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {apiKey.requestCount.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">Rate Limit</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {apiKey.rateLimit.toLocaleString()}/day
                    </p>
                  </div>
                </div>

                <div className="mt-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Permissions:</p>
                  <div className="flex flex-wrap gap-2">
                    {apiKey.permissions.map((perm) => (
                      <span
                        key={perm}
                        className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400 rounded text-xs font-medium"
                      >
                        {perm}
                      </span>
                    ))}
                  </div>
                </div>

                {apiKey.expiresAt && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Expires on {apiKey.expiresAt}
                  </div>
                )}
              </div>

              <div className="flex gap-2 ml-4">
                {apiKey.status === 'active' && (
                  <button
                    onClick={() => handleRevokeKey(apiKey.id)}
                    className="px-3 py-2 border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    Revoke
                  </button>
                )}
                {apiKey.status === 'revoked' && (
                  <button
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-400 rounded cursor-not-allowed"
                    disabled
                  >
                    Revoked
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Documentation */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6">
        <div className="flex items-start gap-3">
          <svg className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">API Documentation</h3>
            <p className="text-sm text-blue-800 dark:text-blue-400 mb-3">
              Learn how to authenticate and use the GP Ads API in your applications.
            </p>
            <a
              href="/docs/api"
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
            >
              View API Documentation →
            </a>
          </div>
        </div>
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create API Key</h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Generate a new API key for programmatic access
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Key Name
                </label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Production API Key"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Permissions
                </label>
                <div className="space-y-2">
                  {availablePermissions.map((perm) => (
                    <label
                      key={perm.id}
                      className="flex items-start gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={newKeyPermissions.includes(perm.id)}
                        onChange={() => handleTogglePermission(perm.id)}
                        className="mt-1 rounded border-gray-300 dark:border-gray-600"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">{perm.label}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{perm.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewKeyName('');
                  setNewKeyPermissions(['read']);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateKey}
                disabled={!newKeyName || newKeyPermissions.length === 0}
                className="px-4 py-2 bg-gp-sungold text-white rounded-lg hover:bg-gp-sungold-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Show New Key Modal */}
      {showKeyModal && generatedKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">API Key Created!</h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Copy your API key now - you won't be able to see it again
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    Make sure to copy your API key now. For security reasons, you won't be able to view it again.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your API Key
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-3 bg-gray-100 dark:bg-gray-900 rounded font-mono text-sm text-gray-900 dark:text-white break-all">
                    {generatedKey}
                  </code>
                  <button
                    onClick={() => handleCopyKey(generatedKey, 'new')}
                    className="px-3 py-3 bg-gp-sungold text-white rounded hover:bg-gp-sungold-600"
                  >
                    {copiedId === 'new' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => {
                  setShowKeyModal(false);
                  setGeneratedKey('');
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                I've Saved My Key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default APIKeysPage;
